// views/Dashboard/PainelExecutivo/sections/marketing/useMarketingDashboardData.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../../../src/services/api';
import { calcularJanelaPeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import { janelaParaDatasDia } from '../../../../../src/lib/dashboard/janelaParaDatasDia';
import { computeByDay, aggregateKpis } from '../../../../../src/lib/marketing/agregacaoDiaria';
import type { MarketingMetaDailyMetric } from '../../../../../types';
import type { PeriodoDashboard } from '../../types';

// `periodo` null = hook não busca nada e devolve estado zerado — usado pro
// Período B do comparativo, que só existe quando o usuário liga o comparativo
// (ver Task 5 do Plano 6). Regra dos Hooks do React: chame este hook sempre,
// nunca dentro de um `if` — passe `null` quando não quiser buscar.
export function useMarketingDashboardData(periodo: PeriodoDashboard | null) {
  const [dados, setDados] = useState<MarketingMetaDailyMetric[]>([]);
  const [loading, setLoading] = useState(!!periodo);
  const [erro, setErro] = useState<string | null>(null);
  const hoje = useMemo(() => new Date(), []);

  const janela = useMemo(() => (periodo ? calcularJanelaPeriodoDashboard(periodo, hoje) : null), [periodo, hoje]);

  const carregar = useCallback(async () => {
    if (!periodo) {
      setDados([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      const { inicio, fim } = janelaParaDatasDia(janela, hoje);
      const metrics = await api.marketing.getMetrics(inicio, fim);
      setDados(metrics);
    } catch (e: any) {
      console.error('Erro ao carregar seção Marketing do Painel Executivo:', e);
      setErro(e?.message || 'Erro ao carregar os dados de marketing.');
    } finally {
      setLoading(false);
    }
  }, [periodo, janela, hoje]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const byDay = useMemo(() => computeByDay(dados), [dados]);
  const kpis = useMemo(() => aggregateKpis(byDay), [byDay]);

  return { byDay, kpis, loading, erro, refetch: carregar };
}
